import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import Loader from "./component/Loader/Loader";
import { loadStripe } from "@stripe/stripe-js";
import Home from "./pages/Home/Home";
import axios from "axios";
import PublicRoute from "./protected/PublicRoute";
import ProtectedRoute from "./protected/ProtectedRoute";
import PathTracker from "./protected/PathTracker";
import AuthEventTracker from "./protected/AuthEventTracker";
import PWAStartGate from "./protected/PWAStartGate";
import { UsageProvider } from "./context/UsageContext";
import { NotificationsProvider } from "./context/NotificationsContext";
import { DownloadProvider } from "./pages/DownloadWindow/DownloadContext";
import ScrollToTop from "./component/ScrollTop";
import Seo from "./component/Seo/Seo";
import { trackEvent } from "./lib/mixpanel";

// Route-level code splitting.
//
// Every page below is its own chunk, so a visitor landing on /pricing downloads
// the pricing page rather than the whole site. This matters beyond the usual
// page-weight argument: crawlers that do execute JavaScript give a page a
// limited budget, and a single multi-megabyte bundle is enough for them to time
// out and index nothing.
//
// Home is deliberately NOT lazy. It's the most common entry point, and the one
// route where waiting on a second round-trip would be visible.
const Pricing = lazy(() => import("./pages/Pricing/Pricing"));
const ManageSubscription = lazy(() => import("./pages/Manage Subscription/ManageSubscription"));
const Support = lazy(() => import("./pages/Support/Support"));
const CheckoutWrapper = lazy(() => import("./pages/Checkout/CheckoutWrapper"));
const LegacyCheckoutRedirect = lazy(() => import("./pages/Checkout/LegacyCheckoutRedirect"));
const Download = lazy(() => import("./pages/Download/Download"));
const Demo = lazy(() => import("./pages/Demo/Demo"));
const WebApp = lazy(() => import("./pages/WebApp/WebApp"));
const HowItWorks = lazy(() => import("./pages/Video/HowitWorks"));
const PrivacyPolicy = lazy(() => import("./pages/Policies/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/Policies/TermsOfService"));
const DataProcessingTerms = lazy(() => import("./pages/Policies/DataProcessingTerms"));
const Blog = lazy(() => import("./pages/Blog/Blog"));
const BlogPost = lazy(() => import("./pages/Blog/BlogPost"));
const HelpCategory = lazy(() => import("./pages/Support/HelpCategory"));
const HelpArticle = lazy(() => import("./pages/Support/HelpArticle"));
const Usage = lazy(() => import("./pages/Usage/Usage"));
const SignupSuccess = lazy(() => import("./pages/SignupSuccess/SignupSuccess"));
const SignInSuccessful = lazy(() => import("./pages/SignInSuccessful/SignInSuccessful"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess/PaymentSuccess"));
const DownloadForWindow = lazy(() => import("./pages/DownloadWindow/DownloadForWindow"));
const DownloadForMac = lazy(() => import("./pages/DownloadMac/DownloadForMac"));
const LoginNew = lazy(() => import("./pages/Login/LoginNew"));
const VoiceTypingForGoogleDocs = lazy(() => import("./pages/VoiceTypingForGoogleDocs/VoiceTypingForGoogleDocs"));
const VoiceTypingForSlackAndNotion = lazy(() => import("./pages/VTypingSlackAndNotion/VoiceTypingForSlackAndNotion"));
const VoiceTypingForAI = lazy(() => import("./pages/vTypingForAI/VoiceTypingForAI"));
const HowVoiceTypingWorks = lazy(() => import("./pages/HowVoicetypingWorks/HowVoiceTypingWorks"));
const VoicePromptingAI = lazy(() => import("./pages/DictatingAiPrompts/VoicePromptingAI"));
const VoiceTypingVsBuiltInDictation = lazy(() => import("./pages/TypingVsBuiltinDictation/VoiceTypingVsBuiltInDictation"));
const WriteEmailsWithoutTyping = lazy(() => import("./pages/WriteEmailsWithouttyping/WriteEmailsWithoutTyping"));
const Blogs = lazy(() => import("./pages/Blogs/Blogs"));
const NotFound = lazy(() => import("./pages/NotFound/NotFound"));

const stripePromise = loadStripe(
  "pk_live_51RD0PIHVT97b1nEmemlvpOnBp7tl6oZqJGkzajpMWIqs7F2tvp7eSiRXArBaChnXCqOJKwaHSGaHopIctPsmOnGz00mVwptY14"
);

// const stripePromise = loadStripe(
//   "pk_test_51RD0PIHVT97b1nEmiwArEddRwUTX2K85Y067GU59FAGBaJae9dYxJKY3suSJn1NltmsvP8vKCTxMaNVuUGqcthA000pfzSLyYu"
// );

axios.defaults.withCredentials = true;
function App() {
  // useEffect(() => {
  //   const disableRightClick = (e) => e.preventDefault();

  //   const handleKeyDown = (e) => {
  //     if (
  //       e.key === "F12" ||
  //       (e.ctrlKey && e.shiftKey && e.key === "I") ||
  //       (e.ctrlKey && e.shiftKey && e.key === "C") ||
  //       (e.ctrlKey && e.shiftKey && e.key === "J")
  //     ) {
  //       e.preventDefault();
  //     }
  //   };

  //   const detectContextMenu = (e) => {
  //     e.preventDefault();
  //     console.log("Right-click is disabled");
  //   };

  //   document.addEventListener("contextmenu", detectContextMenu);
  //   document.addEventListener("keydown", handleKeyDown);

  //   const detectDevTools = () => {
  //     const devToolsOpen = /./;
  //     devToolsOpen.toString = function () {
  //       console.log("Developer tools are disabled");
  //     };
  //     console.log(devToolsOpen);
  //   };

  //   detectDevTools();
  //   return () => {
  //     document.removeEventListener("contextmenu", detectContextMenu);
  //     document.removeEventListener("keydown", handleKeyDown);
  //   };
  // }, []);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    trackEvent("app_opened");
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Seo />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={true}
        newestOnTop
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        progressClassName="toastProgress"
        className={"custom-toast-container"}
        limit={3}
      />
      <PathTracker />
      <AuthEventTracker />
      <PWAStartGate />
      <UsageProvider>
        <NotificationsProvider>
          <DownloadProvider>
            <Suspense
              fallback={
                <div className="w-full">
                  <Loader />
                </div>
              }
            >
              <Routes>
              <Route
                path="/"
                element={
                  isLoading ? (
                    <div className="w-full">
                      <Loader />
                    </div>
                  ) : (
                    <div className=" tracking-[-0.015em] ">
                      <Home />
                    </div>
                  )
                }
              />

              <Route path="/demo" element={<Demo />} />
              <Route
                path="/app"
                element={
                  <ProtectedRoute>
                    <WebApp />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pricing"
                element={
                  <div className=" tracking-[-0.015em]">
                    <Pricing />
                  </div>
                }
              />
              <Route
                path="/help"
                element={
                  <div className=" tracking-[-0.015em]">
                    <Support />
                  </div>
                }
              />
              {/* <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginNew />
                </PublicRoute>
              }
            /> */}
              <Route
                path="/download"
                element={
                  <div className=" tracking-[-0.015em] ">
                    <Download />
                  </div>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <ManageSubscription />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/usage"
                element={
                  <ProtectedRoute>
                    <Usage />
                  </ProtectedRoute>
                }
              />
              {/* The URL carries a short-lived checkout token; the Stripe
                  client secret is fetched by the page and never appears in
                  the address bar. */}
              <Route
                path="/checkout/:checkoutToken"
                element={
                  <div className="max-w-screen-2xl mx-auto  ">
                    <CheckoutWrapper stripePromise={stripePromise} />
                  </div>
                }
              />
              {/* Legacy URL still opened by older desktop builds. Swaps the
                  secret for a token and replaces the URL. Remove once all
                  clients open the backend's checkoutUrl. */}
              <Route
                path="/checkout/:planId/:clientSecret"
                element={<LegacyCheckoutRedirect />}
              />

              <Route
                path="/how-it-works"
                element={
                  <div className=" tracking-[-0.015em]">
                    <HowItWorks />
                  </div>
                }
              />

              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/data-processing-terms" element={<DataProcessingTerms />} />

              <Route
                path="/help/topic/:categorySlug"
                element={<HelpCategory />}
              />
              <Route
                path="/help/topic/:categorySlug/:articleSlug"
                element={<HelpArticle />}
              />
              <Route path="/help/all" element={<Blog />} />
              <Route path="/help/:slug" element={<BlogPost />} />

              <Route path="/signup-successful" element={<SignupSuccess />} />

              {/* Desktop SSO handoff: deep-links tokens back into the native app */}
              <Route path="/sign-in-successful" element={<SignInSuccessful />} />

              <Route
                path="/payment-successful"
                element={
                  <ProtectedRoute>
                    <PaymentSuccess />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/windows"
                element={
                  <div className=" tracking-[-0.015em]">
                    <DownloadForWindow />
                  </div>
                }
              />

              <Route
                path="/mac"
                element={
                  <div className=" tracking-[-0.015em]">
                    <DownloadForMac />
                  </div>
                }
              />

              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginNew />
                  </PublicRoute>
                }
              />

              <Route
                path="/voice-typing-for-google-docs"
                element={<VoiceTypingForGoogleDocs />}
              />

              <Route
                path="/voice-typing-for-slack-and-notion"
                element={<VoiceTypingForSlackAndNotion />}
              />

              <Route
                path="/voice-typing-for-ai-chat-prompts"
                element={<VoiceTypingForAI />}
              />

              <Route
                path="/how-ai-voice-typing-works"
                element={<HowVoiceTypingWorks />}
              />

              <Route
                path="/voice-prompting-ai"
                element={<VoicePromptingAI />}
              />

              <Route
                path="/blog/voice-typing-vs-built-in-dictation"
                element={<VoiceTypingVsBuiltInDictation />}
              />

              <Route
                path="/blog/fastest-way-to-write-emails-without-typing"
                element={<WriteEmailsWithoutTyping />}
              />

              <Route path="/blogs" element={<Blogs />} />

              {/* Anything that matches no route above */}
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </DownloadProvider>
        </NotificationsProvider>
      </UsageProvider>
    </BrowserRouter>
  );
}

export default App;
